import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "../../../utils/notify";
import ApiLoader from "../../ui/ApiLoader";
import NotificationCenter from "../../ui/NotificationCenter";
import { DataTable } from "../../common/DataTable";
import {
  fetchDashboardStats,
  fetchDashboardTrend,
  fetchDashboardProviders,
  fetchAssets,
} from "../../../services/axios/AuthApis/DashboardApis";

import {
  Database,
  ShieldCheck,
  TriangleAlert,
  BadgeCheck,
  Shield,
  Lock,
  Bot,
  FileText,
  Settings,
  AlertTriangle,
} from "lucide-react";

// const tabs = [
//   { id: "dashboard", label: "Dashboard", icon: Database },
//   { id: "assets", label: "Assets", icon: Shield },
//   { id: "rules", label: "Rules", icon: FileText },
//   { id: "sensitive", label: "Sensitive Data", icon: Lock },
//   { id: "risks", label: "Risks", icon: AlertTriangle },
//   { id: "compliance", label: "Compliance", icon: BadgeCheck },
//   { id: "reports", label: "Reports", icon: FileText },
//   { id: "settings", label: "Settings", icon: Settings },
// ];

const Dashboard = () => {
  const navigate = useNavigate();

  // ---------------- Active Tab ----------------
  const [activeTab, setActiveTab] = useState("dashboard");

  // ---------------- Loading ----------------
  const [loading, setLoading] = useState(true);

  // ---------------- Dashboard ----------------
  const [dashboardStats, setDashboardStats] = useState({
    total_assets: 0,
    sensitive_assets: 0,
    risk_score: 0,
    compliance_score: 0,
    ai_insight: "",
  });

  const [riskTrend, setRiskTrend] = useState([]);

  const [providers, setProviders] = useState([]);

  // ---------------- Dashboard Loader ----------------
  const loadDashboard = async () => {
    try {
      setLoading(true);

      await fetchAssets("", true).catch((syncError) => {
        console.warn("DSPM asset sync unavailable:", syncError);
      });

      const [statsRes, trendRes, providerRes] = await Promise.all([
        fetchDashboardStats(),
        fetchDashboardTrend(),
        fetchDashboardProviders(),
        await new Promise((resolve) => setTimeout(resolve, 3000)), // minimum 3 sec loader
      ]);

      setDashboardStats(statsRes.data || {});
      setRiskTrend(trendRes.data || []);
      console.log("providerRes =", providerRes);
      console.log("providerRes.data =", providerRes.data);
      console.log("providers type =", typeof providerRes.data);
      console.log("isArray =", Array.isArray(providerRes.data));

      setProviders(providerRes.data || []);
    } catch (err) {
      console.log(err);
      toast.error("Unable to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Load Dashboard ----------------
  useEffect(() => {
    if (activeTab === "dashboard") {
      loadDashboard();
    }
  }, [activeTab]);
  if (loading) {
    return (
      <ApiLoader
        variant="spinner"
        label="Loading Dashboard..."
        className="py-20"
      />
    );
  }
  return (
    <div className="p-2 bg-gray-100 min-h-screen">
      {/* Dashboard */}
      {activeTab === "dashboard" && (
        <>
          {/* KPI Cards + Notifications */}

          <div className="flex flex-col lg:flex-row gap-5 items-stretch">
            <div className="w-full lg:w-[28rem] flex-shrink-0 h-[380px]">
              <NotificationCenter module="ai_dspm" title="DSPM Notifications" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 flex-1">
              <div
                onClick={() => navigate("/ai-dspm?tab=assets")}
                role="button"
                tabIndex={0}
                className="bg-white rounded-xl shadow p-5 cursor-pointer hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-gray-500">Total Assets</h3>

                  <Database className="text-blue-600" />
                </div>

                <p className="text-3xl font-bold mt-4">
                  {dashboardStats.total_assets || 0}
                </p>
              </div>

              <div
                onClick={() => navigate("/ai-dspm?tab=rules")}
                role="button"
                tabIndex={0}
                className="bg-white rounded-xl shadow p-5 cursor-pointer hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-gray-500">Rules</h3>

                  <FileText className="text-indigo-600" />
                </div>

                <p className="text-3xl font-bold mt-4">
                  {dashboardStats.total_rules || 0}
                </p>
              </div>

              <div
                onClick={() => navigate("/ai-dspm?tab=sensitive")}
                role="button"
                tabIndex={0}
                className="bg-white rounded-xl shadow p-5 cursor-pointer hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-gray-500">Sensitive Assets</h3>

                  <ShieldCheck className="text-green-600" />
                </div>

                <p className="text-3xl font-bold mt-4">
                  {dashboardStats.sensitive_assets || 0}
                </p>
              </div>

              <div
                onClick={() => navigate("/ai-dspm?tab=findings")}
                role="button"
                tabIndex={0}
                className="bg-white rounded-xl shadow p-5 cursor-pointer hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-gray-500">Findings</h3>

                  <AlertTriangle className="text-orange-600" />
                </div>

                <p className="text-3xl font-bold mt-4">
                  {dashboardStats.total_findings || 0}
                </p>
              </div>

              <div
                onClick={() => navigate("/ai-dspm?tab=findings")}
                role="button"
                tabIndex={0}
                className="bg-white rounded-xl shadow p-5 cursor-pointer hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-gray-500">Risk Score</h3>

                  <TriangleAlert className="text-red-600" />
                </div>

                <p className="text-3xl font-bold text-red-600 mt-4">
                  {dashboardStats.risk_score}%
                </p>
              </div>

              <div
                onClick={() => navigate("/ai-dspm?tab=compliance")}
                role="button"
                tabIndex={0}
                className="bg-white rounded-xl shadow p-5 cursor-pointer hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-gray-500">Compliance</h3>

                  <BadgeCheck className="text-emerald-600" />
                </div>

                <p className="text-3xl font-bold text-green-600 mt-4">
                  {dashboardStats.compliance_score}%
                </p>
              </div>
            </div>
          </div>

          {/* Feature Cards */}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
            <div className="bg-white rounded-xl shadow p-5">
              <div className="flex justify-between">
                <h2 className="font-bold">Security</h2>

                <Shield className="text-blue-600" />
              </div>

              <p className="mt-3 text-gray-600">
                Monitor organization security posture.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <div className="flex justify-between">
                <h2 className="font-bold">Sensitive Data</h2>

                <Lock className="text-green-600" />
              </div>

              <p className="mt-3 text-gray-600">
                Discover and classify sensitive information.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <div className="flex justify-between">
                <h2 className="font-bold">Risks</h2>

                <TriangleAlert className="text-red-600" />
              </div>

              <p className="mt-3 text-gray-600">
                Monitor critical risks and incidents.
              </p>
            </div>
          </div>

          {/* AI Insights */}

          <div className="bg-white rounded-xl shadow p-5 mt-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">AI Insights</h2>

              <Bot className="text-purple-600" />
            </div>

            <p className="mt-4 text-gray-700">
              {dashboardStats.ai_insight
                ? dashboardStats.ai_insight
                : "No AI Insight Available"}
            </p>
          </div>

          {/* Cloud Providers */}

          <div className="bg-white rounded-xl shadow p-5 mt-6">
            <h2 className="text-xl font-bold mb-4">Cloud Providers</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Array.isArray(providers) ? (
                providers.map((item, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <h3 className="font-semibold uppercase">
                      {item.cloud_provider}
                    </h3>

                    <p className="text-3xl font-bold text-blue-600 mt-2">
                      {item.assets}
                    </p>

                    <p className="text-gray-500">Assets</p>
                  </div>
                ))
              ) : (
                <pre>{JSON.stringify(providers, null, 2)}</pre>
              )}
            </div>
          </div>

          {/* Risk Trend */}

          <div className="bg-white rounded-xl shadow p-5 mt-6">
            <h2 className="text-xl font-bold mb-4">Risk Trend</h2>

            <DataTable
              columns={[
                { accessorKey: "date", header: "Date" },
                {
                  accessorKey: "open_risks",
                  header: "Open Risks",
                  cell: ({ getValue }) => <span className="text-red-600">{getValue()}</span>,
                },
                {
                  accessorKey: "resolved",
                  header: "Resolved",
                  cell: ({ getValue }) => <span className="text-green-600">{getValue()}</span>,
                },
              ]}
              data={riskTrend}
              getRowId={(item, index) => String(item.date || index)}
              emptyMessage="No risk trend data"
              enableColumnVisibility={false}
              maxBodyHeight="65vh"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
